import { AfterViewInit, Component, ElementRef, OnDestroy, inject, input, output } from '@angular/core';
import { AnalyticsService } from '@gs-2.0/core';

/** Default destination when no campaign URL is configured. */
const DEFAULT_CAMPAIGN_URL = 'https://globalswitchboard.io/';

/**
 * Switchboard promotional card displayed on the fund dashboard.
 *
 * Shows a short call-to-action inviting users to learn more about the
 * Switchboard partnership. The redirection icon on the action button is
 * only rendered when {@link showRedirectIcon} is `true` (the dashboard gates
 * this on the fund type so it shows for Charity, Donor, Project and DAF funds).
 *
 * Figma (desktop): https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=9182-63973
 * Figma (mobile): https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=20313-130652
 */
@Component({
  selector: 'app-switch-board',
  standalone: false,
  templateUrl: './switch-board.component.html',
  styleUrls: ['./switch-board.component.scss'],
})
export class SwitchBoardComponent implements AfterViewInit, OnDestroy {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private impressionObserver?: IntersectionObserver;

  /**
   * Translation key for the card title.
   */
  titleKey = input<string>('fund.switchboard.title');

  /**
   * Translation key for the card description.
   */
  descriptionKey = input<string>('fund.switchboard.description');

  /**
   * Translation key for the "Learn More" action label.
   */
  learnMoreKey = input<string>('fund.switchboard.learn_more');

  /**
   * Whether the redirection (open-in-new-tab) icon is visible.
   */
  showRedirectIcon = input<boolean>(true);

  /**
   * URL the "Learn More" button redirects to. Falls back to the global
   * Switchboard site when no campaign URL is configured.
   */
  campaignUrl = input<string>(DEFAULT_CAMPAIGN_URL);

  /**
   * Emitted when the "Learn More About Switchboard" button is clicked.
   */
  learnMoreClicked = output<void>();

  ngAfterViewInit(): void {
    this.impressionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.95) {
          this.analyticsService.trackSwitchboardImpression();
          this.impressionObserver?.disconnect();
        }
      },
      { threshold: 0.95 },
    );
    this.impressionObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.impressionObserver?.disconnect();
  }

  /**
   * Opens the configured campaign URL (or the default Switchboard site) in a
   * new tab and notifies any listeners.
   */
  onLearnMoreClick(): void {
    this.analyticsService.trackSwitchboardClick();
    const url = this.campaignUrl()?.trim() || DEFAULT_CAMPAIGN_URL;
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
    this.learnMoreClicked.emit();
  }
}
